import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Brain, Activity, Zap, Stethoscope, FlaskConical, ScanLine,
  ShieldCheck, Languages, AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";

const SITE = "https://manasneuropsychiatryhospital.in";
const HERO = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/5cd57190e7d3ffe585debdbc407fd2c67b435e8d4f1824710759b075924d92bd.jpeg";
const EEG = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/b4f629092d5bf3f14ea8c2942185ce9411410fe8ca27416895e12e35bbccc54b.jpeg";

const differentiators = [
  {
    icon: ScanLine,
    title: "The brain is examined, not assumed",
    desc: "Before any psychiatric label is applied, we look for what a scan, an EEG or a blood test can explain — a tumour, a seizure focus, an old bleed, an autoimmune process. A label given without that check can follow a person for decades.",
  },
  {
    icon: FlaskConical,
    title: "Reversible causes are ruled out first",
    desc: "Thyroid disease, B12 deficiency, Wilson's disease, uncontrolled diabetes, liver and kidney failure, steroids and other medicines can all produce depression, mania or psychosis. These are treatable at the cause — and routinely missed.",
  },
  {
    icon: Stethoscope,
    title: "Trained specifically for this overlap",
    desc: "Dr. Kamlesh A. Shah holds an MD in Neuropsychiatry — the branch that exists precisely for brain disease presenting as psychiatric illness. Since 1989 this hospital has worked at that junction rather than at either edge of it.",
  },
  {
    icon: Zap,
    title: "Neuromodulation in-house",
    desc: "When medication is limited by a damaged or medically fragile brain, rTMS, tDCS and modified brief pulse therapy are available here in Bhavnagar — not a referral to another city.",
  },
];

const conditionGroups = [
  {
    label: "Epilepsy and seizure-related",
    icon: Activity,
    items: [
      "Depression, anxiety and psychosis occurring alongside epilepsy",
      "Mood and behaviour change caused by anti-seizure medication",
      "Post-ictal confusion, aggression and memory gaps",
      "Dissociative (functional) seizures — fits with a normal EEG",
      "Temporal lobe epilepsy presenting as personality or religious change",
    ],
  },
  {
    label: "After head injury",
    icon: Brain,
    items: [
      "Irritability, short temper and personality change after an accident",
      "Memory, attention and planning difficulty — post-traumatic cognitive impairment",
      "Post-concussion syndrome: headache, dizziness, poor sleep, low mood",
      "Depression, apathy and loss of initiative following brain injury",
      "Post-traumatic epilepsy with behavioural disturbance",
    ],
  },
  {
    label: "After stroke",
    icon: Activity,
    items: [
      "Post-stroke depression — affecting roughly one in three stroke survivors",
      "Apathy mistaken by the family for laziness or stubbornness",
      "Emotional incontinence — uncontrollable crying or laughing",
      "Vascular cognitive impairment and vascular dementia",
      "Anxiety and fear of a second stroke blocking rehabilitation",
    ],
  },
  {
    label: "Parkinson's & movement disorders",
    icon: Brain,
    items: [
      "Depression and anxiety in Parkinson's disease, often preceding the tremor",
      "Visual hallucinations and delusions in advanced disease or from medication",
      "Impulse-control disorders caused by dopamine agonists — gambling, overspending, hypersexuality",
      "Wilson's disease presenting in a young person as psychiatric illness",
      "Tremor, tics and dystonia with associated obsessive symptoms",
    ],
  },
  {
    label: "Memory, delirium & the medically ill",
    icon: Stethoscope,
    items: [
      "Dementia and memory clinic assessment — diagnosis, staging and carer support",
      "Delirium: sudden confusion in ICU, post-operative and elderly medical patients",
      "Behavioural and psychological symptoms of dementia — suspicion, wandering, aggression",
      "Catatonia — not eating, not speaking, not moving, holding odd postures",
      "Psychiatric symptoms in cancer, dialysis, HIV and post-ICU recovery",
    ],
  },
  {
    label: "Organic & functional presentations",
    icon: FlaskConical,
    items: [
      "Autoimmune encephalitis — sudden 'madness' in a previously well young person",
      "Multiple sclerosis with depression, euphoria or cognitive change",
      "Thyroid disease, B12 deficiency and electrolyte disturbance causing psychiatric symptoms",
      "Steroid, anti-tubercular and other drug-induced psychosis",
      "Functional neurological disorder: paralysis, blindness, tremor or fits with normal scans",
    ],
  },
];

const redFlags = [
  "First psychiatric episode after the age of 45, with no earlier history",
  "Sudden onset over days in a previously completely well person",
  "Fever, headache, seizures or neck stiffness alongside the behaviour change",
  "Visual hallucinations rather than voices",
  "Confusion that comes and goes through the day, worse at night",
  "Weakness, numbness, unsteady walking, double vision or loss of bladder control",
  "Rapid memory loss over weeks to months",
  "No response at all to two adequate trials of psychiatric medication",
];

const myths = [
  {
    myth: "Fits mean the person is mentally ill or possessed.",
    fact:
      "Epilepsy is a neurological condition caused by abnormal electrical activity in the brain — not spirit possession, not black magic, and not an intellectual disability. India carries roughly one-sixth of the world's epilepsy burden, yet a large share of patients still reach a faith healer before a doctor, and in parts of the country the treatment gap exceeds 90%. Around 70% of people with epilepsy become seizure-free on consistent medication. Time lost to bhuva and dora is the single most expensive part of this illness.",
  },
  {
    myth: "If the MRI and EEG are normal, the fits and the paralysis must be fake.",
    fact:
      "Functional neurological disorder is a genuine, involuntary malfunction of brain networks — imaging studies show activation patterns clearly different from people pretending. It is diagnosed by positive clinical signs, not by 'nothing else was found', and it is common: around one in five patients in neurology clinics. With explanation, physiotherapy and psychological therapy, roughly half to sixty percent improve substantially within a year. Telling such a patient they are acting is both wrong and harmful.",
  },
  {
    myth: "Low mood after a stroke or a brain injury is natural — nothing can be done.",
    fact:
      "Post-stroke depression affects about a third of survivors and is a direct consequence of the brain injury as much as of the circumstance. Untreated, it slows physical recovery, worsens disability and raises mortality. It responds to treatment, and treating it measurably improves rehabilitation outcomes. 'Of course he is sad, he had a stroke' is the sentence that keeps patients untreated for years.",
  },
  {
    myth: "Forgetfulness in old age is just age — there is nothing to check.",
    fact:
      "Normal ageing slows recall; it does not take away the ability to manage money, find the way home, or recognise family. Some causes of memory loss — B12 deficiency, hypothyroidism, depression, normal-pressure hydrocephalus, chronic subdural haematoma — are genuinely reversible when found early. Even in Alzheimer's disease, early diagnosis buys years of better function and lets the family plan rather than react.",
  },
  {
    myth: "A psychiatric diagnosis does not need scans or blood tests.",
    fact:
      "Most do not. But a first episode after 45, a sudden onset in days, visual hallucinations, fever, fluctuating confusion or any new neurological sign change that answer completely. Autoimmune encephalitis, brain tumours, Wilson's disease and thyroid disorders have all been treated for months as 'schizophrenia' or 'bipolar disorder'. Investigating the brain is not over-caution — it is the difference between a cure and a lifetime of medication.",
  },
  {
    myth: "Behaviour change in Parkinson's disease means dementia has set in.",
    fact:
      "Gambling, overspending, compulsive eating or hypersexuality appearing in a patient on dopamine agonists is usually a medication effect, not a moral collapse or dementia — and it settles when the drug is adjusted. Visual hallucinations likewise need a review of the prescription before any new diagnosis. Families carry enormous guilt about these symptoms that a single medication review can lift.",
  },
];

const steps = [
  { t: "A proper history — including the family's version", d: "What changed, in what order, and over how long. Old prescriptions, discharge summaries, scans and EEG reports are reviewed rather than repeated blindly. Families see what the patient cannot describe." },
  { t: "Neurological and mental state examination", d: "Both are done in the same sitting — gait, tone, reflexes, eye movements and speech alongside mood, thought and cognition. Cognitive screening is formal, not guesswork." },
  { t: "Targeted investigation", d: "MRI/CT, EEG, thyroid function, B12, electrolytes, liver and kidney function, copper studies or autoimmune antibodies — ordered where the clinical picture points, not as a blanket panel." },
  { t: "Treat the cause, then the symptom", d: "A deficiency is corrected, a seizure focus controlled, an offending medicine withdrawn, an autoimmune process referred for immunotherapy. Psychiatric medication is then chosen around the brain disease and the body's other illnesses." },
  { t: "Neuromodulation when medication is limited", d: "In a fragile brain — post-stroke, post-injury, elderly, multiple medical illnesses — rTMS, tDCS or carefully indicated mBPT often achieve what tablets cannot without unacceptable side-effects." },
  { t: "Family training and long-term follow-up", d: "Carers are taught what the illness can and cannot control, how to respond to aggression or wandering, and when to return urgently. Review is scheduled, not left to the next crisis." },
];

const faqs = [
  {
    q: "What exactly is neuropsychiatry — how is it different from psychiatry or neurology?",
    a: "Neurology treats diseases of the brain and nerves; psychiatry treats disorders of mood, thought and behaviour. Neuropsychiatry is the overlap — psychiatric symptoms arising directly from brain disease, and brain-based conditions that present as mental illness. Epilepsy with psychosis, depression after a stroke, hallucinations in Parkinson's disease and sudden behaviour change from autoimmune encephalitis all live in that overlap, and are often shuttled between the two specialities until someone treats both sides at once.",
  },
  {
    q: "Should I see a neurologist first, or come here?",
    a: "If the main problem is behaviour, mood, memory, confusion or psychosis — come here first. We examine neurologically, investigate for brain causes, and refer to a neurologist or neurosurgeon when imaging or the examination calls for it, continuing the psychiatric care alongside. If the main problem is a pure neurological one such as weakness or recurrent seizures without behavioural change, a neurologist is the right first stop and we work with them.",
  },
  {
    q: "My relative suddenly started behaving strangely over a week. Is this a psychiatric emergency?",
    a: "Treat it as a medical emergency until proven otherwise. A truly sudden onset in a previously well person, especially with fever, headache, seizures, confusion that fluctuates, or any new weakness, points towards encephalitis, a bleed, delirium or a drug effect — not primary mental illness. Bring them in the same day. Starting a tranquilliser and waiting is how these diagnoses get missed.",
  },
  {
    q: "Do you have EEG and MRI here?",
    a: "EEG and cognitive assessment are arranged for our patients, and MRI/CT is done at established imaging centres in Bhavnagar with whom we work — we review the films ourselves rather than relying only on the report. What matters is that the right test is ordered for the right reason and interpreted alongside the clinical picture.",
  },
  {
    q: "Can psychiatric medicines be given safely to someone with epilepsy?",
    a: "Yes, with care. Some antidepressants and antipsychotics lower the seizure threshold more than others, and several interact with anti-seizure drugs. The choice, dose and combination are therefore made with the epilepsy in mind. Being told to 'avoid psychiatric treatment because of fits' leaves treatable depression and psychosis untreated — the answer is the right drug, not no drug.",
  },
  {
    q: "After my father's stroke he sits all day and does nothing. Is he depressed or lazy?",
    a: "Neither word may fit. Apathy after a stroke is a distinct consequence of injury to specific brain circuits and can occur with or without depression. It is not a character flaw and the patient usually cannot 'snap out of it' however much he is scolded. It is assessable and treatable, and treating it is often what makes physiotherapy finally work.",
  },
  {
    q: "The scans are normal but the symptoms are real. What now?",
    a: "A normal scan rules out certain diseases; it does not mean nothing is wrong. Functional neurological disorder is diagnosed on positive examination signs — such as symptoms changing with distraction — and is a genuine problem of brain network function. Treatment is explanation, physiotherapy or occupational therapy, and psychological therapy, with good improvement in a substantial proportion of patients. The worst outcome comes from years of being told it is nothing.",
  },
  {
    q: "Will these problems ever fully go away?",
    a: "It depends entirely on the cause. A B12 deficiency, a drug effect, a thyroid problem or a delirium can resolve completely. Epilepsy-related mood disorder is usually well controlled. After a stroke or a head injury, the aim is maximum recovery of function and quality of life rather than erasing the injury. We will tell you honestly which of these applies to your relative at the first consultation.",
  },
  {
    q: "Do you see children and young adults for these problems?",
    a: "Yes — childhood epilepsy with behavioural difficulty, post-encephalitis sequelae, head injury after a road accident, and the young adult brought in with sudden psychosis where autoimmune or metabolic causes must be excluded. The approach is the same: examine and investigate the brain before accepting a lifelong psychiatric label.",
  },
  {
    q: "Where is the hospital and do I need an appointment?",
    a: "Manas Neuropsychiatry Hospital is at 2nd floor, Carlton Square, near Madhavdeep Complex, Kalanala, Bhavnagar — OPD Monday to Saturday, 9 AM to 8 PM. Call +91 94275 60101 to book. Patients travel to us from Sihor, Palitana, Talaja, Mahuva, Botad and Amreli; bring all old reports, scans and prescriptions with you.",
  },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE}/neuropsychiatry`,
      url: `${SITE}/neuropsychiatry`,
      name: "Neuropsychiatry in Bhavnagar — Brain-Based Psychiatric Care",
      headline: "When the illness is in the brain, not just the mind.",
      description:
        "Neuropsychiatry OPD at Manas Neuropsychiatry Hospital, Bhavnagar — epilepsy-related psychiatric problems, post-stroke and post head-injury behaviour change, Parkinson's, dementia, delirium, autoimmune encephalitis and functional neurological disorder.",
      inLanguage: "en-IN",
      specialty: "Psychiatry",
      primaryImageOfPage: { "@type": "ImageObject", url: HERO },
      about: [
        { "@type": "MedicalSpecialty", name: "Neuropsychiatry" },
        { "@type": "MedicalCondition", name: "Epilepsy with psychiatric comorbidity" },
        { "@type": "MedicalCondition", name: "Post-stroke depression" },
        { "@type": "MedicalCondition", name: "Traumatic brain injury sequelae" },
        { "@type": "MedicalCondition", name: "Parkinson's disease psychosis" },
        { "@type": "MedicalCondition", name: "Delirium" },
        { "@type": "MedicalCondition", name: "Autoimmune encephalitis" },
        { "@type": "MedicalCondition", name: "Functional neurological disorder" },
        { "@type": "MedicalCondition", name: "Catatonia" },
      ],
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Neuropsychiatry", item: `${SITE}/neuropsychiatry` },
        ],
      },
      publisher: { "@id": `${SITE}/#organization` },
      provider: { "@id": `${SITE}/#organization` },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function Neuropsychiatry() {
  useSEO({
    title: "Neuropsychiatry in Bhavnagar — Brain-Based Psychiatric Care",
    description:
      "Neuropsychiatry OPD at Manas Neuropsychiatry Hospital, Bhavnagar — psychiatric problems caused by brain disease: epilepsy, stroke, head injury, Parkinson's, dementia, delirium, autoimmune encephalitis and functional neurological disorder. MD Neuropsychiatry, established 1989. Call +91 94275 60101.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "en",
    alternates: [
      { hreflang: "en-IN", href: `${SITE}/neuropsychiatry` },
      { hreflang: "gu-IN", href: `${SITE}/gu/neuropsychiatry` },
      { hreflang: "x-default", href: `${SITE}/neuropsychiatry` },
    ],
  });
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="neuropsychiatry-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="eyebrow text-[#A39171]">Neuropsychiatry · Since 1989</p>
          <Link
            to="/gu/neuropsychiatry"
            data-testid="np-switch-gujarati"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-3.5 h-3.5" /> ગુજરાતીમાં વાંચો
          </Link>
        </div>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          When the illness is in the brain, <span className="italic text-[#E07A5F]">not only in the mind</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          Epilepsy with depression. Rage after a head injury. A father who sits silently since his stroke.
          A young woman who became psychotic in four days. These are not failures of character or faith —
          they are brain conditions, and they are the reason this hospital has the word neuropsychiatry in
          its name.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="np-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            Book a Consultation <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="np-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            Call +91 94275 60101
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="A neuropsychiatrist reviewing an MRI film with a patient and his family at Manas Hospital, Bhavnagar" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">MD Neuropsychiatry · Kalanala, Bhavnagar</p>
            <p className="font-display text-2xl md:text-3xl leading-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              The scan is read with the patient in the room — not posted as a label.
            </p>
          </div>
        </div>
      </section>

      {/* WHY HERE */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">What a general clinic cannot do</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-6 max-w-3xl">
            A psychiatric label should be <span className="italic text-[#E07A5F]">the last step, not the first</span>.
          </h2>
          <p className="text-lg text-[#5C524E] max-w-3xl leading-relaxed mb-14">
            Brain tumours, autoimmune encephalitis, Wilson's disease, thyroid disorders and vitamin
            deficiencies have all been treated for months as schizophrenia or bipolar disorder. The
            difference is not luck — it is whether anybody examined the brain before the prescription.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {differentiators.map((d, i) => (
              <div key={d.title} data-testid={`np-diff-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                  <d.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight leading-tight">{d.title}</h3>
                <p className="text-[#5C524E] mt-4 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">What we treat</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            Conditions that sit <span className="italic text-[#E07A5F]">between two specialities</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {conditionGroups.map((g, i) => (
              <div key={g.label} data-testid={`np-group-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(163,145,113,0.12)] transition-all">
                <g.icon className="w-7 h-7 text-[#E07A5F] mb-5" />
                <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight mb-5 leading-snug">{g.label}</h3>
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[#5C524E] text-sm leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/conditions/epilepsy-depression" data-testid="np-link-epilepsy" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              Epilepsy with depression guide <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/conditions/post-stroke-depression" data-testid="np-link-poststroke" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              Post-stroke depression guide <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/conditions/dementia" data-testid="np-link-dementia" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              Dementia & geriatric psychiatry guide <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/conditions/schizophrenia" data-testid="np-link-schizophrenia" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              Schizophrenia & psychosis guide <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/general-psychiatry" data-testid="np-link-gp" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              General psychiatry OPD <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* RED FLAGS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">Warning signs</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              When psychiatric symptoms <span className="italic text-[#E07A5F]">need a brain work-up</span>.
            </h2>
            <p className="text-[#5C524E] mt-6 leading-relaxed">
              Most mental illness needs no scan at all. But any of the following should stop a prescription
              and start an investigation — bring the patient in rather than waiting for the next review.
            </p>
            <div className="mt-8 rounded-3xl overflow-hidden aspect-[4/3]">
              <img src={EEG} alt="EEG recording being performed at Manas Neuropsychiatry Hospital, Bhavnagar" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-10 relative overflow-hidden" data-testid="np-red-flags">
              <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
              <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center mb-6 relative">
                <AlertTriangle className="w-5 h-5" />
              </span>
              <ul className="space-y-4 relative">
                {redFlags.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[#FDFBF7]/90">
                    <XCircle className="w-4 h-4 text-[#E2C044] mt-1 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-[#FDFBF7]/60 relative">
                Sudden confusion with fever, headache or seizures is a medical emergency. Come the same day,
                or call +91 94275 60101.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MYTHS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Myth & fact</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            What families are told, <span className="italic text-[#E07A5F]">and what is true</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {myths.map((m, i) => (
              <div key={m.myth} data-testid={`np-myth-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
                <div className="flex items-start gap-3 mb-5 pb-5 border-b border-[#A39171]/15">
                  <XCircle className="w-5 h-5 text-[#A39171] mt-1 shrink-0" />
                  <p className="font-display text-xl text-[#A39171] leading-snug">{m.myth}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#E07A5F] mt-1 shrink-0" />
                  <p className="text-[#2D2623] leading-relaxed">{m.fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Our approach</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14">
            How a neuropsychiatric case <span className="italic text-[#E07A5F]">is actually worked up</span>.
          </h2>
          <ol className="space-y-10">
            {steps.map((s, i) => (
              <li key={s.t} data-testid={`np-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3"><span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span></div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight leading-snug">{s.t}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Frequently asked</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            Questions families bring <span className="italic text-[#E07A5F]">to the first visit</span>.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`np-${i}`} data-testid={`np-faq-${i}`}
                className="bg-[#FDFBF7] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5 leading-snug">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
            Bring the old reports. <span className="italic text-[#E07A5F]">We will read them properly</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            Scans, EEGs, discharge summaries and every prescription tried so far — the answer is often sitting
            in papers nobody has reviewed together.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="np-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">Request Appointment</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">Call +91 94275 60101</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
